import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Loader2, Stethoscope, BookOpen, Pencil, Check, X, Clock, ChevronDown, LogIn, LogOut, Eye, MousePointerClick, FileText, Award, UserCheck, UserX, GitMerge, Search, AlertTriangle, ArrowRight, ClipboardList, Download, Dot } from "lucide-react";
import { toast } from "@/lib/toastBanner";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatDateTime(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return ts; }
}

const EVENT_LABELS = {
  login: "Logged in",
  logout: "Logged out",
  dashboard_viewed: "Viewed dashboard",
  track_viewed: "Viewed track",
  track_clicked: "Opened track",
  track_submitted: "Submitted track",
  lesson_viewed: "Viewed lesson",
  lesson_opened: "Opened lesson",
  lesson_completed: "Completed lesson",
  lexp_opened: "Opened learning experience",
  lexp_completed: "Completed learning experience",
  welcome_dismissed: "Dismissed welcome",
  item_answered: "Answered assessment item",
  epic_practice_viewed: "Viewed Epic Practice",
  credential_generated: "Generated credential",
  resources_page_viewed: "Viewed resources",
  resource_clicked: "Opened resource",
  learning_experiences_viewed: "Viewed learning experiences",
  standalone_lexp_viewed: "Viewed LEXP",
  results_viewed: "Viewed results",
};

function formatEventLabel(type) {
  return EVENT_LABELS[type] || type.replace(/_/g, " ");
}

function formatEventMeta(meta) {
  const parts = [];
  if (meta.track_name) parts.push(meta.track_name);
  if (meta.lexp_title) parts.push(meta.lexp_title);
  if (meta.role_name) parts.push(meta.role_name);
  if (meta.resource_title) parts.push(meta.resource_title);
  if (meta.email) parts.push(meta.email);
  if (meta.cohort_name) parts.push(meta.cohort_name);
  if (meta.correct !== undefined) parts.push(meta.correct ? "Correct" : "Incorrect");
  return parts.join(" · ");
}

function EventIcon({ type }) {
  const cls = "h-3.5 w-3.5";
  if (type === "login") return <LogIn className={cls} style={{ color: "#22c55e" }} />;
  if (type === "logout") return <LogOut className={cls} style={{ color: "#ef4444" }} />;
  if (type?.includes("completed") || type?.includes("submitted")) return <Award className={cls} style={{ color: "#f59e0b" }} />;
  if (type?.includes("viewed")) return <Eye className={cls} style={{ color: "#6E7787" }} />;
  if (type?.includes("clicked") || type?.includes("opened") || type?.includes("generated")) return <MousePointerClick className={cls} style={{ color: "var(--primary-color, #4A90D9)" }} />;
  if (type?.includes("answered")) return <FileText className={cls} style={{ color: "#8b5cf6" }} />;
  return <Clock className={cls} style={{ color: "#6E7787" }} />;
}

export default function LearnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const label = settings.learner_label || "Learner";
  const [learner, setLearner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gradebook, setGradebook] = useState([]);
  const [gbLoading, setGbLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editScore, setEditScore] = useState("");
  const [editMixed, setEditMixed] = useState({ ext_correct: "", ext_total: "" });
  const [editingLexpId, setEditingLexpId] = useState(null);
  const [editLexpScore, setEditLexpScore] = useState("");
  const [expandedAssessment, setExpandedAssessment] = useState(null);
  const [expandedLexp, setExpandedLexp] = useState(null);
  const [assessmentItems, setAssessmentItems] = useState({});
  const [lexpItems, setLexpItems] = useState({});
  const [itemsLoading, setItemsLoading] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemValue, setEditingItemValue] = useState("");
  // External Item entry modal (default-correct workflow)
  const [extItemOpen, setExtItemOpen] = useState(false);
  const [extItemCtx, setExtItemCtx] = useState(null); // { lexpId, cohortId, lexpTitle }
  const [extItemList, setExtItemList] = useState([]);
  const [extItemIncorrect, setExtItemIncorrect] = useState(new Set());
  const [extItemLoading, setExtItemLoading] = useState(false);
  const [extItemSaving, setExtItemSaving] = useState(false);
  const [finalizingLexp, setFinalizingLexp] = useState(null);
  const [finalizingCohort, setFinalizingCohort] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "" });
  const [editSaving, setEditSaving] = useState(false);
  // Cohort enrollment management (inside Edit dialog)
  const [availableCohorts, setAvailableCohorts] = useState([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);
  const [addCohortId, setAddCohortId] = useState("");
  const [cohortMutating, setCohortMutating] = useState(false);

  // Merge state
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeStep, setMergeStep] = useState("search"); // search | preview | result
  const [mergeSearch, setMergeSearch] = useState("");
  const [mergeResults, setMergeResults] = useState([]);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);

  const resetMerge = () => {
    setMergeStep("search");
    setMergeSearch("");
    setMergeResults([]);
    setMergeSource(null);
    setMergePreview(null);
    setMergeResult(null);
  };

  const openMerge = () => { resetMerge(); setMergeOpen(true); };

  const searchForSource = async () => {
    if (!mergeSearch.trim()) return;
    setMergeSearching(true);
    try {
      const { data } = await axios.get(`${API}/learners?search=${encodeURIComponent(mergeSearch.trim())}`, { withCredentials: true });
      setMergeResults(data.filter((c) => c.id !== id && c.status !== "merged"));
    } catch { toast.error("Search failed"); }
    finally { setMergeSearching(false); }
  };

  const selectSource = async (source) => {
    setMergeSource(source);
    setMergePreviewLoading(true);
    setMergeStep("preview");
    try {
      const { data } = await axios.get(`${API}/learners/${id}/merge-preview?source_id=${source.id}`, { withCredentials: true });
      setMergePreview(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load preview"); setMergeStep("search"); }
    finally { setMergePreviewLoading(false); }
  };

  const executeMerge = async () => {
    setMerging(true);
    try {
      const { data } = await axios.post(`${API}/learners/${id}/merge`, { source_id: mergeSource.id }, { withCredentials: true });
      setMergeResult(data);
      setMergeStep("result");
      toast.success("Merge completed successfully");
      fetchLearner();
      fetchGradebook();
      fetchEvents(0);
    } catch (err) { toast.error(err.response?.data?.detail || "Merge failed"); }
    finally { setMerging(false); }
  };

  const openEdit = () => {
    setEditForm({ first_name: learner.first_name || "", last_name: learner.last_name || "", email: learner.email || "" });
    setAddCohortId("");
    setEditOpen(true);
    // Lazy-load the org's active cohort list so the picker is populated.
    setCohortsLoading(true);
    axios.get(`${API}/cohorts?status=active`, { withCredentials: true })
      .then(({ data }) => setAvailableCohorts(Array.isArray(data) ? data : []))
      .catch(() => setAvailableCohorts([]))
      .finally(() => setCohortsLoading(false));
  };

  // Enrolled cohorts derived from the loaded learner + names catalog. We
  // union the active `cohort_ids` on the learner doc with the names fetched
  // from /cohorts so we can render `{id, name}` chips even if a name isn't
  // in the picker list (e.g. archived-since-enrollment).
  const activeEnrollments = useMemo(() => {
    if (!learner) return [];
    const nameById = new Map((availableCohorts || []).map((c) => [c.id, c.name]));
    const fallbackNames = (learner.cohort_name || "").split(",").map((s) => s.trim()).filter(Boolean);
    let fallbackIdx = 0;
    return ((learner.cohort_ids || []) || [])
      .filter((e) => e && e.status === "active" && e.cohort_id)
      .map((e) => ({
        cohort_id: e.cohort_id,
        name: nameById.get(e.cohort_id) || fallbackNames[fallbackIdx++] || e.cohort_id,
      }));
  }, [learner, availableCohorts]);

  const pickableCohorts = useMemo(() => {
    const enrolled = new Set(activeEnrollments.map((e) => e.cohort_id));
    return (availableCohorts || []).filter((c) => !enrolled.has(c.id));
  }, [availableCohorts, activeEnrollments]);

  const addCohort = async () => {
    if (!addCohortId) { toast.error("Select a cohort"); return; }
    setCohortMutating(true);
    try {
      await axios.post(
        `${API}/cohorts/${addCohortId}/learners`,
        {
          first_name: learner.first_name || "",
          last_name: learner.last_name || "",
          email: learner.email || "",
        },
        { withCredentials: true },
      );
      const { data } = await axios.get(`${API}/learners/${id}`, { withCredentials: true });
      setLearner(data);
      setAddCohortId("");
      toast.success("Cohort added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add cohort");
    } finally {
      setCohortMutating(false);
    }
  };

  const removeCohort = async (cohortId) => {
    if (!window.confirm("Remove learner from this cohort? Their historical assessments and gradebook data will be preserved but they'll be marked as archived in this cohort.")) return;
    setCohortMutating(true);
    try {
      await axios.delete(`${API}/cohorts/${cohortId}/learners/${id}`, { withCredentials: true });
      const { data } = await axios.get(`${API}/learners/${id}`, { withCredentials: true });
      setLearner(data);
      toast.success("Cohort removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove cohort");
    } finally {
      setCohortMutating(false);
    }
  };

  const saveEdit = async () => {
    if (!editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.email.trim()) {
      toast.error("All fields are required");
      return;
    }
    setEditSaving(true);
    try {
      const { data } = await axios.patch(`${API}/learners/${id}`, editForm, { withCredentials: true });
      setLearner(data);
      setEditOpen(false);
      toast.success("Details updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setEditSaving(false);
    }
  };

  const fetchLearner = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/learners/${id}`, {
        withCredentials: true,
      });
      setLearner(data);
    } catch {
      navigate("/admin/learners");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchLearner();
  }, [fetchLearner]);

  const fetchGradebook = useCallback(async () => {
    setGbLoading(true);
    try {
      // Cache-bust + explicit no-cache headers so a save → refetch never gets
      // a stale response from intermediate caches. Without this the row can
      // sit on "Awaiting scores" even though the DB and the modal both have
      // the freshly-saved data.
      const { data } = await axios.get(
        `${API}/learners/${id}/gradebook?_=${Date.now()}`,
        { withCredentials: true, headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } },
      );
      setGradebook(data.cohorts || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[LearnerDetail] fetchGradebook failed", err?.response?.status, err?.response?.data);
      toast.error("Could not refresh gradebook");
    }
    finally { setGbLoading(false); }
  }, [id]);

  useEffect(() => { fetchGradebook(); }, [fetchGradebook]);

  const exportLearnerGradebook = () => {
    if (!learner || gradebook.length === 0) return;
    const name = `${learner.first_name}_${learner.last_name}`.replace(/\s+/g, "_");
    const csvRows = [];
    csvRows.push(["Cohort", "Assessment", "Scoring Mode", "Native Correct", "Native Total", "Ext Correct", "Ext Total", "Score", "Total Score %"].join(","));
    for (const cohort of gradebook) {
      for (const e of (cohort.entries || [])) {
        csvRows.push([
          `"${cohort.cohort_name}"`,
          `"${e.assessment_type_name}"`,
          e.scoring_mode || "standard",
          e.native_correct != null ? e.native_correct : "",
          e.native_total != null ? e.native_total : "",
          e.external_correct != null ? e.external_correct : "",
          e.external_total != null ? e.external_total : "",
          e.has_score ? (e.score ?? "") : "",
          e.total_score != null ? e.total_score : "",
        ].join(","));
      }
    }
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `gradebook_${name}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const fetchEvents = useCallback(async (offset = 0) => {
    setEventsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/learners/${id}/events?limit=20&offset=${offset}`, { withCredentials: true });
      setEvents((prev) => offset === 0 ? data.events : [...prev, ...data.events]);
      setEventsTotal(data.total);
    } catch { /* silent */ }
    finally { setEventsLoading(false); }
  }, [id]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const toggleStatus = async () => {
    const newStatus = learner.status === "active" ? "inactive" : "active";
    setTogglingStatus(true);
    try {
      await axios.patch(`${API}/learners/${id}/status`, { status: newStatus }, { withCredentials: true });
      setLearner((prev) => ({ ...prev, status: newStatus }));
      toast.success(`${label} ${newStatus === "active" ? "activated" : "deactivated"}`);
    } catch { toast.error("Failed to update status"); }
    finally { setTogglingStatus(false); }
  };

  const saveMixedScore = async (assessmentId) => {
    const ec = parseFloat(editMixed.ext_correct);
    const et = parseFloat(editMixed.ext_total);
    if (isNaN(ec) || ec < 0) { toast.error("Ext Correct must be >= 0"); return; }
    if (isNaN(et) || et < 0) { toast.error("Ext Total must be >= 0"); return; }
    try {
      await axios.put(`${API}/learners/${id}/gradebook`, {
        assessment_id: assessmentId, ext_correct: ec, ext_total: et,
      }, { withCredentials: true });
      fetchGradebook();
      setEditingId(null);
      toast.success("Score updated");
    } catch (err) { toast.error(err?.response?.data?.detail || `Save failed (${err?.response?.status || "network"})`); }
  };

  const saveScore = async (assessmentId) => {
    const val = parseFloat(editScore);
    if (isNaN(val) || val < 0 || val > 100) { toast.error("Score must be 0–100"); return; }
    try {
      await axios.put(`${API}/learners/${id}/gradebook`, { assessment_id: assessmentId, score: val }, { withCredentials: true });
      fetchGradebook();
      setEditingId(null);
      toast.success("Score updated");
    } catch (err) { toast.error(err?.response?.data?.detail || `Save failed (${err?.response?.status || "network"})`); }
  };

  const saveLexpScore = async (lexpId, cohortId, maxScore) => {
    const val = parseFloat(editLexpScore);
    if (isNaN(val) || val < 0 || val > (maxScore || 100)) { toast.error(`Score must be 0–${maxScore || 100}`); return; }
    try {
      await axios.put(`${API}/learners/${id}/gradebook`, { lexp_id: lexpId, cohort_id: cohortId, external_score: val }, { withCredentials: true });
      fetchGradebook();
      setEditingLexpId(null);
      toast.success("External score saved (preliminary — click Finalize to release to learner)");
    } catch (err) { toast.error(err?.response?.data?.detail || `Save failed (${err?.response?.status || "network"})`); }
  };

  const finalizeLexp = async (lexpId, cohortId) => {
    setFinalizingLexp(`${lexpId}_${cohortId}`);
    try {
      await axios.post(`${API}/learners/${id}/gradebook/lexp/${lexpId}/finalize`, { cohort_id: cohortId }, { withCredentials: true });
      toast.success("Score finalized — visible to learner");
      fetchGradebook();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Finalize failed");
    } finally { setFinalizingLexp(null); }
  };

  const openExtItemEntry = async (lexpId, cohortId, lexpTitle) => {
    setExtItemCtx({ lexpId, cohortId, lexpTitle });
    setExtItemOpen(true);
    setExtItemLoading(true);
    setExtItemIncorrect(new Set());
    try {
      const { data } = await axios.get(
        `${API}/learners/${id}/gradebook/lexp/${lexpId}/external-items`,
        { withCredentials: true }
      );
      const list = data.items || [];
      setExtItemList(list);
      // Seed "incorrect" set from existing responses that are already marked incorrect
      const seed = new Set(list.filter((it) => it.has_response && it.current_correct === false).map((it) => it.item_id));
      setExtItemIncorrect(seed);
    } catch { toast.error("Failed to load external items"); setExtItemOpen(false); }
    finally { setExtItemLoading(false); }
  };

  const toggleExtItemIncorrect = (itemId) => {
    setExtItemIncorrect((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const saveExtItemEntry = async () => {
    if (!extItemCtx) return;
    setExtItemSaving(true);
    try {
      const { data: saveResp } = await axios.post(
        `${API}/learners/${id}/gradebook/lexp/${extItemCtx.lexpId}/external-items`,
        { cohort_id: extItemCtx.cohortId, incorrect_item_ids: Array.from(extItemIncorrect) },
        { withCredentials: true }
      );
      toast.success(`Saved & finalized (${extItemIncorrect.size} marked incorrect)`);
      const savedLexpId = extItemCtx.lexpId;
      const savedCohortId = extItemCtx.cohortId;
      const newScore = saveResp?.score || {};
      setExtItemOpen(false);
      setExtItemCtx(null);
      // ── Optimistic update so the row flips to "Finalized" before the
      //    GET refetch returns. The authoritative refresh below corrects
      //    any drift.
      setGradebook((prev) => prev.map((cohort) => {
        if (cohort.cohort_id !== savedCohortId) return cohort;
        return {
          ...cohort,
          lexp_entries: (cohort.lexp_entries || []).map((le) => {
            if (le.lexp_id !== savedLexpId) return le;
            return {
              ...le,
              is_finalized: true,
              is_preliminary: false,
              scores_entered: true,
              has_score: true,
              final_score: newScore.final_score ?? le.final_score,
              native_score: newScore.native_score ?? le.native_score,
              native_max: newScore.native_max ?? le.native_max,
              external_item_score: newScore.external_item_score ?? le.external_item_score,
              external_item_max: newScore.external_item_max ?? le.external_item_max,
              external_score: newScore.external_score ?? le.external_score,
              external_max: newScore.external_max ?? le.external_max,
              aggregate_external_score: newScore.external_score ?? le.aggregate_external_score,
              aggregate_external_max: newScore.external_max ?? le.aggregate_external_max,
            };
          }),
        };
      }));
      // Invalidate the per-item cache for this LEXP so the expanded panel
      // re-fetches Correct/Incorrect chips next time it's opened.
      setLexpItems((prev) => {
        const next = { ...prev };
        delete next[savedLexpId];
        return next;
      });
      // ── Awaited authoritative refresh — bridges the loading spinner to
      //    the fresh response.
      await fetchGradebook();
      // If the LEXP detail was expanded, re-fetch its items immediately so
      // the chips populate without a manual collapse/expand.
      if (expandedLexp === savedLexpId) {
        setItemsLoading(savedLexpId);
        try {
          const { data } = await axios.get(
            `${API}/learners/${id}/gradebook/lexp/${savedLexpId}/items?_=${Date.now()}`,
            { withCredentials: true, headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } },
          );
          setLexpItems((prev) => ({ ...prev, [savedLexpId]: data.items || [] }));
        } finally { setItemsLoading(null); }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setExtItemSaving(false); }
  };

  const finalizeAllInCohort = async (cohortId, preliminaryCount) => {
    if (!window.confirm(`Finalize ${preliminaryCount} preliminary LEXP${preliminaryCount === 1 ? "" : "s"} in this cohort? External items with no response will default to correct. Learners will see scores immediately.`)) return;
    setFinalizingCohort(cohortId);
    try {
      const { data } = await axios.post(
        `${API}/learners/${id}/gradebook/cohort/${cohortId}/finalize-all`,
        {},
        { withCredentials: true }
      );
      toast.success(`Finalized ${data.finalized_count} LEXP${data.finalized_count === 1 ? "" : "s"}`);
      // Bulk finalize writes admin-entered responses for every unresponded
      // external item. Blow the per-item cache so any expanded LEXP shows
      // the fresh Correct/Incorrect chips rather than stale "No response".
      setLexpItems({});
      fetchGradebook();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk finalize failed");
    } finally { setFinalizingCohort(null); }
  };

  const toggleAssessmentExpand = async (assessmentId) => {
    if (expandedAssessment === assessmentId) { setExpandedAssessment(null); return; }
    setExpandedAssessment(assessmentId);
    if (assessmentItems[assessmentId]) return; // cached
    setItemsLoading(assessmentId);
    try {
      const { data } = await axios.get(`${API}/learners/${id}/gradebook/${assessmentId}/items`, { withCredentials: true });
      setAssessmentItems((prev) => ({ ...prev, [assessmentId]: data.items || [] }));
    } catch { toast.error("Failed to load items"); }
    finally { setItemsLoading(null); }
  };

  const updateItemResponse = async (assessmentId, itemId, correct, earnedOverride) => {
    try {
      await axios.put(`${API}/learners/${id}/gradebook/${assessmentId}/items/${itemId}`, {
        correct,
        ...(earnedOverride !== undefined ? { earned_points: earnedOverride } : {}),
      }, { withCredentials: true });
      setAssessmentItems((prev) => ({
        ...prev,
        [assessmentId]: (prev[assessmentId] || []).map((it) =>
          it.item_id === itemId ? {
            ...it,
            correct,
            has_response: true,
            earned_points: earnedOverride !== undefined ? earnedOverride : (correct ? it.point_value : 0),
          } : it
        ),
      }));
      setEditingItemId(null);
      fetchGradebook();
      toast.success("Response updated");
    } catch { toast.error("Update failed"); }
  };

  const toggleLexpExpand = async (lexpId) => {
    if (expandedLexp === lexpId) { setExpandedLexp(null); return; }
    setExpandedLexp(lexpId);
    if (lexpItems[lexpId]) return;
    setItemsLoading(lexpId);
    try {
      const { data } = await axios.get(`${API}/learners/${id}/gradebook/lexp/${lexpId}/items`, { withCredentials: true });
      setLexpItems((prev) => ({ ...prev, [lexpId]: data.items || [] }));
    } catch { toast.error("Failed to load items"); }
    finally { setItemsLoading(null); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  if (!learner) return null;

  return (
    <div className="space-y-6 min-w-0" data-testid="learner-detail-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/admin/learners")}
        data-testid="learner-back-btn"
        className="text-[#A1A9B8] hover:text-white hover:bg-white/5 w-fit"
      >
        <ArrowLeft className="h-4 w-4 mr-1.5" />
        Back to {label}s
      </Button>

      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <div
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-full flex items-center justify-center text-lg font-bold text-white shrink-0"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          {learner.first_name?.[0]}{learner.last_name?.[0]}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white tracking-tight truncate" data-testid="learner-detail-name">
            {learner.first_name} {learner.last_name}
          </h2>
          <p className="text-[#6E7787] font-body text-sm truncate">{learner.email}</p>
        </div>
        <div className="flex items-center gap-2">
        <Badge
          data-testid="learner-status-badge"
          className={
            learner.status === "active"
              ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
              : learner.status === "merged"
              ? "bg-purple-500/15 text-purple-400 border-purple-500/20"
              : "bg-red-500/15 text-red-400 border-red-500/20"
          }
        >
          {learner.status === "active" ? "Active" : learner.status === "merged" ? "Merged" : "Inactive"}
        </Badge>
        <Button
          variant="outline"
          size="sm"
          onClick={toggleStatus}
          disabled={togglingStatus || learner.status === "merged"}
          data-testid="learner-toggle-status-btn"
          className={`border-white/10 text-xs h-8 ${
            learner.status === "active"
              ? "text-red-400 hover:text-red-300 hover:bg-red-500/10"
              : "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
          }`}
        >
          {togglingStatus ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : learner.status === "active" ? <UserX className="h-3 w-3 mr-1.5" /> : <UserCheck className="h-3 w-3 mr-1.5" />}
          {learner.status === "active" ? "Deactivate" : "Activate"}
        </Button>
        {learner.status !== "merged" && (
          <Button
            variant="outline"
            size="sm"
            onClick={openMerge}
            data-testid="merge-learner-btn"
            className="border-white/10 text-xs h-8 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
          >
            <GitMerge className="h-3 w-3 mr-1.5" />
            Merge Learner
          </Button>
        )}
        </div>
      </div>

      <div className="bg-[#11141A] border border-white/10 rounded-xl p-6 space-y-5">
        <div className="flex items-center gap-2 pb-4 border-b border-white/10">
          <Stethoscope className="h-5 w-5" style={{ color: "var(--primary-color, #4A90D9)" }} />
          <h3 className="font-body text-lg font-bold text-white">{label} Details</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={openEdit}
            data-testid="edit-learner-btn"
            className="ml-auto text-[#A1A9B8] hover:text-white hover:bg-white/5 h-7 px-2"
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-1">First Name</p>
            <p className="text-sm text-white font-body" data-testid="learner-first-name">{learner.first_name}</p>
          </div>
          <div>
            <p className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-1">Last Name</p>
            <p className="text-sm text-white font-body" data-testid="learner-last-name">{learner.last_name}</p>
          </div>
          <div>
            <p className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-1">Email</p>
            <p className="text-sm text-white font-body" data-testid="learner-email">{learner.email}</p>
          </div>
          <div>
            <p className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-1">Active Cohorts</p>
            <p className="text-sm font-body" data-testid="learner-cohort">
              {learner.cohort_name ? (
                <span className="text-white">{learner.cohort_name}</span>
              ) : (
                <span className="text-[#6E7787]">None</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-1">Registered</p>
            <p className="text-sm text-white font-body">{formatDate(learner.created_at)}</p>
          </div>
          <div>
            <p className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-1">Last Updated</p>
            <p className="text-sm text-white font-body">{formatDate(learner.updated_at)}</p>
          </div>
        </div>

        {/* Test Flag Toggle (inside Learner Details) */}
        </div>

      {/* Gradebook */}
      <div className="rounded-xl p-4 sm:p-6 space-y-5" style={{ backgroundColor: "#fff", border: "1px solid #e2e6ef" }} data-testid="gradebook-section">
        <div className="flex items-center justify-between gap-2 pb-4 flex-wrap" style={{ borderBottom: "1px solid #e2e6ef" }}>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" style={{ color: "#062546" }} />
            <h3 className="font-body text-lg font-bold" style={{ color: "#062546" }}>Gradebook</h3>
          </div>
          {gradebook.length > 0 && (
            <Button onClick={exportLearnerGradebook} variant="outline" size="sm"
              className="text-xs h-8 gap-1.5"
              style={{ borderColor: "#e2e6ef", color: "#5a6577" }}
              data-testid="export-learner-gradebook-btn">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </Button>
          )}
        </div>
        {gbLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin" style={{ color: "#8896a7" }} /></div>
        ) : gradebook.length === 0 ? (
          <p className="text-sm font-body text-center py-4" style={{ color: "#8896a7" }}>No data found for this learner.</p>
        ) : (
          <div className="space-y-4">
            {gradebook.map((cohort) => (
              <div key={cohort.cohort_id} className="rounded-xl overflow-hidden" style={{ border: "1px solid #e2e6ef", backgroundColor: "#fff" }} data-testid={`gb-cohort-${cohort.cohort_id}`}>
                {/* Cohort header - matches Curriculum Manager track card */}
                {(() => {
                  const preliminaryCount = (cohort.lexp_entries || []).filter((l) => l.is_preliminary).length;
                  return (
                    <div className="group flex items-center gap-3 px-4 py-3.5 select-none flex-wrap" style={{ borderBottom: "1px solid #e2e6ef" }}>
                      <div className="w-1 self-stretch rounded-full shrink-0" style={{ backgroundColor: "#062546" }} />
                      <span className="text-base font-body font-bold flex-1" style={{ color: "#062546" }}>{cohort.cohort_name}</span>
                      {cohort.track_total != null && (
                        <div
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg shrink-0"
                          style={{ backgroundColor: "#e6faf8", border: "1px solid #99f6e4" }}
                          data-testid={`gb-track-total-${cohort.cohort_id}`}
                        >
                          <span className="text-[10px] font-body font-semibold uppercase tracking-wider" style={{ color: "#0d5a52" }}>Overall Score</span>
                          <span className="text-lg font-heading font-bold tabular-nums whitespace-nowrap" style={{ color: "#0d9488" }}>{cohort.track_total}%</span>
                          {cohort.overall_correct != null && (
                            <span className="text-[10px] font-body tabular-nums" style={{ color: "#0d5a52" }}>
                              {cohort.overall_correct}/{cohort.overall_total}
                            </span>
                          )}
                        </div>
                      )}
                      {/* "Finalize All" deliberately removed from the learner
                          view — it's a cohort-level operation. Single-LEXP
                          finalize remains on each row. */}
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-body font-medium whitespace-nowrap"
                        style={{ backgroundColor: cohort.status === "active" ? "#e6faf8" : "#fef3cd", color: cohort.status === "active" ? "#0d9488" : "#92751a" }}>
                        {cohort.status}
                      </span>
                    </div>
                  );
                })()}

                <div className="px-3 py-2">
                  {cohort.entries && cohort.entries.length > 0 && (
                    <div className="mb-3">
                      <div className="flex items-center gap-1.5 mb-2 px-2">
                        <ClipboardList className="h-3.5 w-3.5" style={{ color: "#8896a7" }} />
                        <span className="text-[10px] font-body font-semibold uppercase tracking-wider" style={{ color: "#8896a7" }}>Historical Assessments</span>
                      </div>
                      <div className="divide-y" style={{ borderColor: "#f0f2f7" }}>
                        {cohort.entries.map((e, eIdx) => {
                          const isMixed = e.scoring_mode === "mixed";
                          const isEditing = editingId === e.assessment_id;
                          const displayScore = isMixed ? e.total_score : e.score;
                          const beginEdit = () => {
                            setEditingId(e.assessment_id);
                            if (isMixed) {
                              setEditMixed({
                                ext_correct: String(e.external_correct ?? ""),
                                ext_total: String(e.external_total ?? ""),
                              });
                            } else {
                              setEditScore(e.has_score ? String(displayScore ?? "") : "");
                            }
                          };
                          const doSave = () => isMixed ? saveMixedScore(e.assessment_id) : saveScore(e.assessment_id);
                          return (
                            <div
                              key={e.assessment_id}
                              className="flex flex-wrap items-center gap-x-3 gap-y-2 px-2 py-3"
                              data-testid={`learner-gb-historical-row-${cohort.cohort_id}-${e.assessment_id}`}
                            >
                              <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-body font-semibold"
                                style={{ backgroundColor: "transparent", color: "#8896a7", border: "1.5px solid #c5cbd6" }}>
                                {eIdx + 1}
                              </span>
                              <div className="flex-1 min-w-[140px]">
                                <p className="text-sm font-body font-semibold" style={{ color: "#062546" }}>{e.assessment_type_name}</p>
                                {(e.linked_lexp_ids || []).length > 0 && (
                                  <p className="text-[11px] font-body mt-0.5" style={{ color: "#0891b2" }}>Linked</p>
                                )}
                                {isMixed && e.has_score && !isEditing && (
                                  <p className="text-[11px] font-body mt-0.5 tabular-nums" style={{ color: "#8896a7" }}>
                                    N:{e.native_correct ?? 0}/{e.native_total ?? 0} · E:{e.external_correct ?? 0}/{e.external_total ?? 0}
                                  </p>
                                )}
                              </div>
                              {!isEditing && (
                                <span
                                  className="font-bold shrink-0 text-right tabular-nums whitespace-nowrap min-w-[56px]"
                                  style={{ color: e.has_score ? "#0d9488" : "#c5cbd6" }}
                                  data-testid={`learner-gb-historical-score-${cohort.cohort_id}-${e.assessment_id}`}
                                >
                                  {e.has_score ? `${displayScore}%` : "\u2014"}
                                </span>
                              )}
                              {isEditing ? (
                                isMixed ? (
                                  <div className="flex flex-wrap items-center gap-2 shrink-0 basis-full sm:basis-auto justify-end">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px] font-body" style={{ color: "#8896a7" }}>Ext Correct</span>
                                      <Input type="number" min="0" step="1" value={editMixed.ext_correct}
                                        onChange={(ev) => setEditMixed({ ...editMixed, ext_correct: ev.target.value })}
                                        onKeyDown={(ev) => { if (ev.key === "Enter") doSave(); if (ev.key === "Escape") setEditingId(null); }}
                                        autoFocus className="h-8 text-xs text-center w-20 rounded-md"
                                        style={{ backgroundColor: "#fff", borderColor: "#e2e6ef", color: "#062546" }}
                                        data-testid={`learner-gb-historical-input-ext-correct-${e.assessment_id}`} />
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px] font-body" style={{ color: "#8896a7" }}>Ext Total</span>
                                      <Input type="number" min="0" step="1" value={editMixed.ext_total}
                                        onChange={(ev) => setEditMixed({ ...editMixed, ext_total: ev.target.value })}
                                        onKeyDown={(ev) => { if (ev.key === "Enter") doSave(); if (ev.key === "Escape") setEditingId(null); }}
                                        className="h-8 text-xs text-center w-20 rounded-md"
                                        style={{ backgroundColor: "#fff", borderColor: "#e2e6ef", color: "#062546" }}
                                        data-testid={`learner-gb-historical-input-ext-total-${e.assessment_id}`} />
                                    </div>
                                    <button onClick={doSave} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#0d9488", borderColor: "#99f6e4" }}>
                                      <Check className="h-3.5 w-3.5" />
                                    </button>
                                    <button onClick={() => setEditingId(null)} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#8896a7", borderColor: "#e2e6ef" }}>
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 shrink-0 basis-full sm:basis-auto justify-end">
                                    <Input
                                      type="number" min="0" max="100" step="0.01" value={editScore}
                                      onChange={(ev) => setEditScore(ev.target.value)}
                                      onKeyDown={(ev) => { if (ev.key === "Enter") doSave(); if (ev.key === "Escape") setEditingId(null); }}
                                      autoFocus
                                      className="h-8 text-xs text-center flex-1 sm:flex-none sm:w-24 rounded-md"
                                      style={{ backgroundColor: "#fff", borderColor: "#e2e6ef", color: "#062546" }}
                                      data-testid={`learner-gb-historical-input-${e.assessment_id}`}
                                    />
                                    <button onClick={doSave} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#0d9488", borderColor: "#99f6e4" }}>
                                      <Check className="h-3.5 w-3.5" />
                                    </button>
                                    <button onClick={() => setEditingId(null)} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#8896a7", borderColor: "#e2e6ef" }}>
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                )
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={beginEdit}
                                  className="h-8 px-3 text-xs font-body shrink-0"
                                  style={{ borderColor: "#c5cbd6", color: "#062546", backgroundColor: "transparent" }}
                                  data-testid={`learner-gb-historical-update-btn-${e.assessment_id}`}
                                >
                                  <Pencil className="h-3 w-3 mr-1" /> Update Score
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {cohort.lexp_entries && cohort.lexp_entries.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 mb-2 px-2">
                        <BookOpen className="h-3.5 w-3.5" style={{ color: "#8896a7" }} />
                        <span className="text-[10px] font-body font-semibold uppercase tracking-wider" style={{ color: "#8896a7" }}>Learning Experiences</span>
                      </div>
                      {cohort.lexp_entries.map((le, leIdx) => {
                        const lexpKey = `${le.lexp_id}_${cohort.cohort_id}`;
                        const showExternal = le.scoring_method !== "native_only";
                        const isPreliminary = !!le.is_preliminary;
                        const hasExternalItems = !!le.has_external_items;
                        const hasExternalScoring = !!le.has_external_scoring;
                        return (
                          <React.Fragment key={lexpKey}>
                            <div className="group flex flex-wrap items-center gap-x-3 gap-y-2 py-3 px-3 cursor-pointer transition-colors rounded-lg hover:bg-black/[0.02]"
                              onClick={() => le.has_item_data && toggleLexpExpand(le.lexp_id)} data-testid={`gb-lexp-row-${le.lexp_id}`}>
                              {le.has_item_data ? (
                                <span className="shrink-0 w-4 h-4 flex items-center justify-center transition-transform" style={{ color: "#8896a7", transform: expandedLexp === le.lexp_id ? "rotate(180deg)" : "rotate(0deg)" }}>
                                  <ChevronDown className="h-3.5 w-3.5" />
                                </span>
                              ) : <span className="w-4 shrink-0" />}
                              <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-body font-semibold"
                                style={{ backgroundColor: expandedLexp === le.lexp_id ? "#062546" : "transparent", color: expandedLexp === le.lexp_id ? "#fff" : "#8896a7", border: expandedLexp === le.lexp_id ? "none" : "1.5px solid #c5cbd6" }}>
                                {leIdx + 1}
                              </span>
                              <div className="flex-1 min-w-[140px] flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-body font-semibold" style={{ color: "#062546" }}>{le.lexp_title}</span>
                                {hasExternalScoring && (
                                  <span
                                    className="text-[11px] font-body shrink-0"
                                    style={{ color: "#8896a7" }}
                                    data-testid={`gb-lexp-subtitle-${le.lexp_id}`}
                                  >
                                    · {hasExternalItems ? `External items · ${le.external_item_count ?? 0} items` : "External score"}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 sm:gap-4 text-xs font-body shrink-0 ml-auto sm:ml-0 flex-wrap justify-end">
                                {/* Unified Items breakdown — sums every scored
                                    source (native MC/SATA, external_item, and
                                    the aggregate external_score channel) into
                                    one combined "Items: earned/total" pill so
                                    every LEXP type renders identically. */}
                                {(() => {
                                  // Always sum every available source — even
                                  // when preliminary — so the admin can see
                                  // the breakdown they've entered before
                                  // finalizing. The percentage on the right
                                  // already gates on isPreliminary.
                                  const parts = [];
                                  let earned = 0;
                                  let total = 0;
                                  if (le.native_score != null && le.native_max != null) {
                                    earned += Number(le.native_score) || 0;
                                    total += Number(le.native_max) || 0;
                                    parts.push("native");
                                  }
                                  if (le.external_item_score != null && le.external_item_max != null) {
                                    earned += Number(le.external_item_score) || 0;
                                    total += Number(le.external_item_max) || 0;
                                    parts.push("external");
                                  }
                                  if (le.aggregate_external_score != null && le.aggregate_external_max != null) {
                                    earned += Number(le.aggregate_external_score) || 0;
                                    total += Number(le.aggregate_external_max) || 0;
                                    parts.push("aggregate");
                                  }
                                  // Fallback: legacy field name for pure-aggregate LEXPs.
                                  if (parts.length === 0 && le.external_score != null && le.external_max != null) {
                                    earned += Number(le.external_score) || 0;
                                    total += Number(le.external_max) || 0;
                                    parts.push("legacy");
                                  }
                                  if (total <= 0) return null;
                                  // Round to 1 decimal max so the chip stays clean.
                                  const fmt = (n) => Number.isInteger(n) ? n : Math.round(n * 100) / 100;
                                  return (
                                    <span style={{ color: "#8896a7" }} data-testid={`gb-lexp-items-${le.lexp_id}`}>
                                      Items: <span style={{ color: "#062546" }}>{fmt(earned)}/{fmt(total)}</span>
                                    </span>
                                  );
                                })()}
                                <span className="font-bold text-right tabular-nums whitespace-nowrap min-w-[56px]" style={{ color: (le.final_score != null && !isPreliminary) ? "#0d9488" : "#c5cbd6" }}>
                                  {(le.final_score != null && !isPreliminary) ? `${le.final_score}%` : "\u2014"}
                                </span>
                              </div>
                              {/* Action UI — Edit Score → Finalize → Update Score */}
                              {hasExternalScoring && !isPreliminary && (
                                /* Already finalized → Finalized badge + Update Score button */
                                <div className="flex items-center gap-2 shrink-0 basis-full sm:basis-auto justify-end" onClick={(ev) => ev.stopPropagation()}>
                                  <Badge className="text-[10px] gap-1" style={{ backgroundColor: "#e6faf8", color: "#0d9488", border: "1px solid #99f6e4" }}>
                                    <Check className="h-2.5 w-2.5" /> Finalized
                                  </Badge>
                                  {hasExternalItems && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openExtItemEntry(le.lexp_id, cohort.cohort_id, le.lexp_title)}
                                      className="h-8 px-3 text-xs font-body"
                                      style={{ borderColor: "#c5cbd6", color: "#062546", backgroundColor: "transparent" }}
                                      data-testid={`gb-lexp-update-score-btn-${le.lexp_id}`}
                                    >
                                      <Pencil className="h-3 w-3 mr-1" /> Update Score
                                    </Button>
                                  )}
                                </div>
                              )}
                              {isPreliminary && hasExternalScoring && (
                                <div className="flex items-center gap-2 shrink-0 basis-full sm:basis-auto justify-end" onClick={(ev) => ev.stopPropagation()}>
                                  {editingLexpId === lexpKey ? (
                                    <>
                                      <Input type="number" min="0" max={le.external_max || 100} step="0.01" value={editLexpScore} onChange={(ev) => setEditLexpScore(ev.target.value)} onKeyDown={(ev) => { if (ev.key === "Enter") saveLexpScore(le.lexp_id, cohort.cohort_id, le.external_max); if (ev.key === "Escape") setEditingLexpId(null); }} autoFocus className="h-8 text-sm text-center w-24 font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} />
                                      <button onClick={() => saveLexpScore(le.lexp_id, cohort.cohort_id, le.external_max)} className="h-7 w-7 rounded flex items-center justify-center border" style={{ color: "#0d9488", borderColor: "#99f6e4" }}><Check className="h-3.5 w-3.5" /></button>
                                      <button onClick={() => setEditingLexpId(null)} className="h-7 w-7 rounded flex items-center justify-center border" style={{ color: "#8896a7", borderColor: "#e2e6ef" }}><X className="h-3.5 w-3.5" /></button>
                                    </>
                                  ) : (
                                    <>
                                      <span
                                        className="text-xs font-body w-28 text-right"
                                        style={{ color: "#8896a7" }}
                                        data-testid={`gb-lexp-status-${le.lexp_id}`}
                                      >
                                        {le.scores_entered ? "Scores entered" : "Awaiting scores"}
                                      </span>
                                      {le.scores_entered ? (
                                        <>
                                          {hasExternalItems && (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => openExtItemEntry(le.lexp_id, cohort.cohort_id, le.lexp_title)}
                                              className="h-8 px-3 text-xs font-body"
                                              style={{ borderColor: "#c5cbd6", color: "#062546", backgroundColor: "transparent" }}
                                              data-testid={`gb-lexp-edit-score-btn-${le.lexp_id}`}
                                            >
                                              <Pencil className="h-3 w-3 mr-1" /> Edit Score
                                            </Button>
                                          )}
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => finalizeLexp(le.lexp_id, cohort.cohort_id)}
                                            disabled={finalizingLexp === `${le.lexp_id}_${cohort.cohort_id}`}
                                            className="h-8 px-3 text-xs font-body"
                                            style={{ borderColor: "#0d9488", color: "#0d9488", backgroundColor: "transparent" }}
                                            data-testid={`gb-lexp-finalize-btn-${le.lexp_id}`}
                                          >
                                            {finalizingLexp === `${le.lexp_id}_${cohort.cohort_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" />Finalize</>}
                                          </Button>
                                        </>
                                      ) : (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            if (hasExternalItems) {
                                              openExtItemEntry(le.lexp_id, cohort.cohort_id, le.lexp_title);
                                            } else {
                                              setEditingLexpId(lexpKey);
                                              setEditLexpScore(le.external_score != null ? String(le.external_score) : "");
                                            }
                                          }}
                                          className="h-8 px-3 text-xs font-body"
                                          style={{ borderColor: "#c5cbd6", color: "#062546", backgroundColor: "transparent" }}
                                          data-testid={`gb-lexp-enter-scores-btn-${le.lexp_id}`}
                                        >
                                          <Pencil className="h-3 w-3 mr-1" /> Enter Scores
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            {expandedLexp === le.lexp_id && (
                              <div className="ml-7 mr-2 mb-2 rounded-lg" style={{ backgroundColor: "#f8f9fc", border: "1px solid #e2e6ef" }}>
                                <div className="px-4 py-3">
                                  {itemsLoading === le.lexp_id ? (
                                    <div className="flex justify-center py-3"><Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: "#8896a7" }} /></div>
                                  ) : (lexpItems[le.lexp_id] || []).length === 0 ? (
                                    <p className="text-xs font-body py-2" style={{ color: "#8896a7" }}>No item data available.</p>
                                  ) : (
                                    <div className="space-y-0.5">
                                      {(lexpItems[le.lexp_id] || []).map((item) => (
                                        <div key={item.item_id} className="flex items-center gap-3 py-1.5 px-1 rounded hover:bg-black/[0.02]" data-testid={`gb-lexp-item-${item.item_id}`}>
                                          <Dot className="h-4 w-4 shrink-0" style={{ color: "#c5cbd6" }} />
                                          <span className="flex-1 text-xs font-body" style={{ color: "#5a6577" }}>{item.title}</span>
                                          <span className="text-xs font-body" style={{ color: item.has_response ? (item.correct ? "#0d9488" : "#dc2626") : "#c5cbd6" }}>
                                            {item.item_category === "survey" ? "Survey" : item.has_response ? (item.correct ? `Correct (${item.earned_points}/${item.point_value})` : `Incorrect (${item.earned_points}/${item.point_value})`) : "No response"}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}


                  {(!cohort.entries || cohort.entries.length === 0) && (!cohort.lexp_entries || cohort.lexp_entries.length === 0) && (
                    <p className="text-xs font-body pl-2" style={{ color: "#8896a7" }}>No assessments or learning experiences in this cohort.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Activity Log (separate container) ── */}
      <div className="bg-[#11141A] border border-white/10 rounded-xl" data-testid="activity-log">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" style={{ color: "var(--primary-color, #4A90D9)" }} />
              <h3 className="font-body text-sm font-bold text-white">Activity Log</h3>
            </div>
            <span className="text-xs text-[#6E7787] font-body">{eventsTotal} event{eventsTotal !== 1 ? "s" : ""}</span>
          </div>
          {events.length === 0 && !eventsLoading ? (
            <div className="px-5 py-8 text-center text-[#6E7787] text-sm font-body">No activity recorded yet</div>
          ) : (
            <div className="divide-y divide-white/5">
              {events.map((ev, idx) => (
                <div key={`${ev.event_type}-${ev.timestamp}-${idx}`} className="px-5 py-3 flex items-start gap-3" data-testid={`event-row-${idx}`}>
                  <div className="shrink-0 mt-0.5">
                    <EventIcon type={ev.event_type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-body text-white">
                      {formatEventLabel(ev.event_type)}
                      {ev.metadata?.lexp_title && <span className="text-[#A1A9B8]"> — {ev.metadata.lexp_title}</span>}
                    </p>
                    {ev.metadata && Object.keys(ev.metadata).length > 0 && formatEventMeta(ev.metadata) && (
                      <p className="text-xs text-[#6E7787] font-body mt-0.5 truncate">
                        {formatEventMeta(ev.metadata)}
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] text-[#6E7787] font-body shrink-0 mt-0.5 whitespace-nowrap">{formatDateTime(ev.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
          {eventsLoading && <div className="px-5 py-3 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto" style={{ color: "var(--primary-color, #4A90D9)" }} /></div>}
          {events.length < eventsTotal && !eventsLoading && (
            <div className="px-5 py-3 border-t border-white/5 text-center">
              <Button variant="ghost" size="sm" onClick={() => fetchEvents(events.length)} data-testid="load-more-events-btn" className="text-[#A1A9B8] hover:text-white text-xs">
                <ChevronDown className="h-3 w-3 mr-1.5" />
                Load more ({eventsTotal - events.length} remaining)
              </Button>
            </div>
          )}
        </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white sm:max-w-lg w-[min(92vw,32rem)] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Edit {label} Details</DialogTitle>
            <DialogDescription className="text-[#6E7787] text-sm">Update the learner's name and email address.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-1 block">First Name</label>
              <Input
                data-testid="edit-first-name"
                value={editForm.first_name}
                onChange={(e) => setEditForm((p) => ({ ...p, first_name: e.target.value }))}
                className="bg-[#0A0C10] border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-1 block">Last Name</label>
              <Input
                data-testid="edit-last-name"
                value={editForm.last_name}
                onChange={(e) => setEditForm((p) => ({ ...p, last_name: e.target.value }))}
                className="bg-[#0A0C10] border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-1 block">Email</label>
              <Input
                data-testid="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                className="bg-[#0A0C10] border-white/10 text-white"
              />
            </div>

            {/* Cohort enrollments — add/remove */}
            <div className="pt-3 border-t border-white/10" data-testid="edit-cohorts-section">
              <label className="text-xs text-[#6E7787] font-body uppercase tracking-wider mb-2 block">
                Cohort Enrollments
              </label>

              {activeEnrollments.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid="edit-cohorts-current">
                  {activeEnrollments.map((e) => (
                    <span
                      key={e.cohort_id}
                      data-testid={`edit-cohort-chip-${e.cohort_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-xs font-body text-white"
                    >
                      <span className="max-w-[220px] truncate">{e.name}</span>
                      <button
                        type="button"
                        onClick={() => removeCohort(e.cohort_id)}
                        disabled={cohortMutating}
                        data-testid={`edit-cohort-remove-${e.cohort_id}`}
                        className="rounded-full hover:bg-red-500/20 h-4 w-4 flex items-center justify-center text-[#A1A9B8] hover:text-red-300 disabled:opacity-50"
                        aria-label={`Remove from ${e.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#6E7787] font-body mb-3" data-testid="edit-cohorts-empty">
                  Not enrolled in any cohort.
                </p>
              )}

              <div className="flex gap-2 min-w-0">
                <select
                  value={addCohortId}
                  onChange={(e) => setAddCohortId(e.target.value)}
                  disabled={cohortsLoading || cohortMutating || pickableCohorts.length === 0}
                  data-testid="edit-cohort-select"
                  className="flex-1 min-w-0 h-9 rounded-md bg-[#0A0C10] border border-white/10 text-white text-sm font-body px-2 focus:outline-none focus:border-[var(--primary-color,#4A90D9)] disabled:opacity-50"
                >
                  <option value="">
                    {cohortsLoading
                      ? "Loading cohorts…"
                      : pickableCohorts.length === 0
                        ? (availableCohorts.length === 0 ? "No active cohorts available" : "Learner is already in all cohorts")
                        : "-- Select a cohort --"}
                  </option>
                  {pickableCohorts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.cohort_type && c.cohort_type !== "standard" ? ` · ${c.cohort_type}` : ""}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={addCohort}
                  disabled={!addCohortId || cohortMutating}
                  data-testid="edit-cohort-add-btn"
                  style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                  className="h-9 shrink-0"
                >
                  {cohortMutating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
                </Button>
              </div>
              <p className="text-[10px] text-[#6E7787] font-body mt-2 break-words">
                Adding to a standard cohort will be blocked if the learner is already enrolled in another active standard cohort.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setEditOpen(false)} className="text-[#A1A9B8] hover:text-white hover:bg-white/5" data-testid="edit-cancel-btn">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveEdit}
                disabled={editSaving}
                data-testid="edit-save-btn"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
              >
                {editSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge Learner Dialog */}
      <Dialog open={mergeOpen} onOpenChange={(open) => { if (!open) setMergeOpen(false); }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-purple-400" />
              Merge {label}
            </DialogTitle>
            <DialogDescription className="text-[#6E7787] text-sm">
              {mergeStep === "search" && "Search for the source account to absorb into this learner."}
              {mergeStep === "preview" && "Review the data that will be migrated."}
              {mergeStep === "result" && "Merge completed."}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Search */}
          {mergeStep === "search" && (
            <div className="space-y-4 pt-1">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
                  <Input
                    data-testid="merge-search-input"
                    placeholder="Search by name or email..."
                    value={mergeSearch}
                    onChange={(e) => setMergeSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchForSource()}
                    className="bg-[#0A0C10] border-white/10 text-white pl-10"
                  />
                </div>
                <Button size="sm" onClick={searchForSource} disabled={mergeSearching} data-testid="merge-search-btn"
                  style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
                  {mergeSearching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Search"}
                </Button>
              </div>
              {mergeResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-white/10 rounded-lg divide-y divide-white/5">
                  {mergeResults.map((c) => (
                    <button key={c.id} data-testid={`merge-source-${c.id}`}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors text-left"
                      onClick={() => selectSource(c)}>
                      <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                        style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
                        {c.first_name?.[0]}{c.last_name?.[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white font-body truncate">{c.first_name} {c.last_name}</p>
                        <p className="text-xs text-[#6E7787] font-body truncate">{c.email}{c.cohort_name ? ` · ${c.cohort_name}` : ""}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-[#6E7787] shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              {mergeResults.length === 0 && mergeSearch && !mergeSearching && (
                <p className="text-center text-sm text-[#6E7787] py-4">No learners found matching that search.</p>
              )}
            </div>
          )}

          {/* Step 2: Preview */}
          {mergeStep === "preview" && (
            <div className="space-y-4 pt-1">
              {mergePreviewLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
              ) : mergePreview && (
                <>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-[#0A0C10] border border-white/10 overflow-hidden">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-[10px] text-purple-400 font-body uppercase tracking-widest mb-0.5">Source (will be absorbed)</p>
                      <p className="text-sm text-white font-body font-medium truncate">{mergePreview.source.first_name} {mergePreview.source.last_name}</p>
                      <p className="text-xs text-[#6E7787] truncate">{mergePreview.source.email}</p>
                      {mergePreview.source.cohort_names?.length > 0 && (
                        <p className="text-xs text-[#6E7787] mt-0.5 truncate">Cohorts: {mergePreview.source.cohort_names.join(", ")}</p>
                      )}
                    </div>
                    <ArrowRight className="h-5 w-5 text-purple-400 shrink-0" />
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-[10px] text-emerald-400 font-body uppercase tracking-widest mb-0.5">Target (will keep)</p>
                      <p className="text-sm text-white font-body font-medium truncate">{mergePreview.target.first_name} {mergePreview.target.last_name}</p>
                      <p className="text-xs text-[#6E7787] truncate">{mergePreview.target.email}</p>
                      {mergePreview.target.cohort_names?.length > 0 && (
                        <p className="text-xs text-[#6E7787] mt-0.5 truncate">Cohorts: {mergePreview.target.cohort_names.join(", ")}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <p className="text-xs text-[#6E7787] font-body uppercase tracking-wider">Data to migrate</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        ["Assessment Scores", mergePreview.counts.assessment_scores],
                        ["Item Responses", mergePreview.counts.item_responses],
                        ["LEXP Responses", mergePreview.counts.learner_item_responses],
                        ["Track Submissions", mergePreview.counts.track_submissions],
                        ["LEXP Completions", mergePreview.counts.lexp_completions],
                        ["Assessment Completions", mergePreview.counts.assessment_completions],
                        ["Activity Events", mergePreview.counts.user_events],
                        ["Welcome States", mergePreview.counts.welcome_states],
                      ].filter(([, v]) => v > 0).map(([label, count]) => (
                        <div key={label} className="flex justify-between px-3 py-1.5 rounded bg-[#0A0C10] text-xs">
                          <span className="text-[#A1A9B8]">{label}</span>
                          <span className="text-white font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                    {mergePreview.new_cohorts?.length > 0 && (
                      <div className="px-3 py-1.5 rounded bg-emerald-500/10 text-xs text-emerald-400">
                        + New cohorts: {mergePreview.new_cohorts.join(", ")}
                      </div>
                    )}
                    {mergePreview.total_records === 0 && (
                      <p className="text-xs text-[#6E7787] text-center py-2">No data records to migrate from this account.</p>
                    )}
                  </div>

                  <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 overflow-hidden">
                    <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-300/90 font-body min-w-0">
                      <p className="font-medium mb-1">This action cannot be undone.</p>
                      <p className="break-words">The source account (<span className="break-all">{mergePreview.source.email}</span>) will be marked as "merged" and deactivated. All data transfers to <span className="break-all">{mergePreview.target.email}</span>. Duplicate records keep the target's data.</p>
                    </div>
                  </div>

                  <div className="flex justify-between pt-1">
                    <Button variant="ghost" size="sm" onClick={() => setMergeStep("search")} className="text-[#A1A9B8] hover:text-white hover:bg-white/5" data-testid="merge-back-btn">
                      <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Back
                    </Button>
                    <Button size="sm" onClick={executeMerge} disabled={merging} data-testid="merge-confirm-btn"
                      className="bg-purple-600 hover:bg-purple-500 text-white">
                      {merging ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <GitMerge className="h-3.5 w-3.5 mr-1.5" />}
                      Confirm Merge
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: Result */}
          {mergeStep === "result" && mergeResult && (
            <div className="space-y-4 pt-1">
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                <Check className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-emerald-300 font-body font-medium">{mergeResult.message}</p>
              </div>
              <div className="space-y-1.5">
                <p className="text-xs text-[#6E7787] font-body uppercase tracking-wider">Migration Summary</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {Object.entries(mergeResult.migrated).filter(([, v]) => v > 0).map(([key, val]) => (
                    <div key={key} className="flex justify-between px-3 py-1.5 rounded bg-[#0A0C10] text-xs">
                      <span className="text-[#A1A9B8]">{key.replace(/_/g, " ")}</span>
                      <span className="text-emerald-400 font-medium">{val} migrated</span>
                    </div>
                  ))}
                  {Object.entries(mergeResult.skipped).filter(([, v]) => v > 0).map(([key, val]) => (
                    <div key={`skip-${key}`} className="flex justify-between px-3 py-1.5 rounded bg-[#0A0C10] text-xs">
                      <span className="text-[#A1A9B8]">{key.replace(/_/g, " ")}</span>
                      <span className="text-amber-400 font-medium">{val} skipped</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end pt-1">
                <Button size="sm" onClick={() => setMergeOpen(false)} data-testid="merge-done-btn"
                  style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* External Item Entry Modal — default-correct workflow */}
      <Dialog open={extItemOpen} onOpenChange={(open) => { if (!open) { setExtItemOpen(false); setExtItemCtx(null); } }}>
        <DialogContent className="bg-white text-[#062546] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ext-item-entry-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2" style={{ color: "#062546" }}>
              <ClipboardList className="h-5 w-5" style={{ color: "#0d9488" }} />
              Score External Items
            </DialogTitle>
            <DialogDescription className="text-sm font-body" style={{ color: "#5a6577" }}>
              {extItemCtx?.lexpTitle}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="p-3 rounded-lg text-xs font-body" style={{ backgroundColor: "#e6faf8", color: "#0d5a52", border: "1px solid #99f6e4" }}>
              <p className="font-medium mb-1">All items default to correct.</p>
              <p>Check only the items the learner got wrong. Saving will finalize the score and make it visible to the learner.</p>
            </div>
            {extItemLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0d9488" }} /></div>
            ) : extItemList.length === 0 ? (
              <p className="text-sm font-body text-center py-4" style={{ color: "#8896a7" }}>No external items in this LEXP.</p>
            ) : (
              <div className="border rounded-lg divide-y" style={{ borderColor: "#e2e6ef" }}>
                {extItemList.map((it) => {
                  const marked = extItemIncorrect.has(it.item_id);
                  return (
                    <label
                      key={it.item_id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${marked ? "bg-red-50" : "hover:bg-gray-50"}`}
                      data-testid={`ext-item-row-${it.item_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={marked}
                        onChange={() => toggleExtItemIncorrect(it.item_id)}
                        className="h-4 w-4 shrink-0"
                        style={{ accentColor: "#dc2626" }}
                        data-testid={`ext-item-checkbox-${it.item_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-body font-medium truncate" style={{ color: "#062546" }}>{it.title || `Item ${it.item_id.slice(0, 6)}`}</p>
                        <p className="text-[11px] font-body" style={{ color: "#8896a7" }}>{it.point_value} pt{it.point_value !== 1 ? "s" : ""}</p>
                      </div>
                      <span
                        className="text-[11px] font-body font-medium px-2 py-0.5 rounded-full shrink-0"
                        style={marked
                          ? { backgroundColor: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca" }
                          : { backgroundColor: "#e6faf8", color: "#0d9488", border: "1px solid #99f6e4" }
                        }
                      >
                        {marked ? "Incorrect" : "Correct"}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {!extItemLoading && extItemList.length > 0 && (
              <div className="text-xs font-body" style={{ color: "#5a6577" }}>
                {extItemList.length - extItemIncorrect.size} correct · {extItemIncorrect.size} incorrect
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setExtItemOpen(false)} data-testid="ext-item-cancel-btn" style={{ color: "#5a6577" }}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveExtItemEntry}
                disabled={extItemSaving || extItemLoading || extItemList.length === 0}
                data-testid="ext-item-save-btn"
                style={{ backgroundColor: "#0d9488", color: "#fff" }}
              >
                {extItemSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
                Save & Finalize
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
